import * as React from "react";
import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { DefaultButton } from "@fluentui/react/lib/Button";
import type { Accessorial } from "@src/types/api";

interface AccessorialsPickerProps {
  accessorials: Accessorial[];
  onToggle: Function;
}

export function AccessorialsPicker({ accessorials, onToggle }: AccessorialsPickerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const orderedAccessorials = useMemo(() => {
    return [...accessorials].sort((a, b) => {
      if (a.checked === b.checked) return 0;
      return a.checked ? -1 : 1;
    });
  }, [accessorials]);
  const visibleAccessorials = useMemo(
    () => (isExpanded ? orderedAccessorials : orderedAccessorials.slice(0, 6)),
    [isExpanded, orderedAccessorials]
  );
  const hiddenCount = orderedAccessorials.length - 6;

  return (
    <Box className="shipment-accessorials-section" sx={styles.section}>
      <Box className="accessorials-container" sx={styles.container}>
        {visibleAccessorials.map((acc) => (
          <DefaultButton
            key={acc.key}
            toggle
            checked={acc.checked}
            text={acc.key}
            onClick={() => onToggle(acc.key)}
            allowDisabledFocus
            styles={styles.chipButton}
          />
        ))}
        {!isExpanded && hiddenCount > 0 && (
          <DefaultButton
            text="More options ▾"
            onClick={() => setIsExpanded(true)}
            allowDisabledFocus
            styles={styles.moreButton}
          />
        )}
        {isExpanded && hiddenCount > 0 && (
          <DefaultButton
            text="Fewer options ▴"
            onClick={() => setIsExpanded(false)}
            allowDisabledFocus
            styles={styles.moreButton}
          />
        )}
      </Box>
    </Box>
  );
}

const styles = {
  section: {},
  container: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  chipButton: {
    root: {
      backgroundColor: "transparent",
      borderColor: "#d2d0ce",
      borderWidth: "1px",
      borderRadius: "16px",
      color: "#605e5c",
      fontSize: "12px",
      fontWeight: "400",
      padding: "4px 12px",
      minWidth: "auto",
      maxWidth: "none",
      transition: "all 0.15s ease",
    },
    rootHovered: {
      backgroundColor: "#f3f2f1",
      borderColor: "#c8c6c4",
      color: "#323130",
    },
    rootChecked: {
      backgroundColor: "#0078d4",
      borderColor: "#0078d4",
      color: "white",
      fontWeight: "500",
    },
    rootCheckedHovered: {
      backgroundColor: "#106ebe",
      borderColor: "#106ebe",
    },
  },
  moreButton: {
    root: {
      backgroundColor: "transparent",
      borderColor: "#d2d0ce",
      borderWidth: "1px",
      borderRadius: "16px",
      color: "#605e5c",
      fontSize: "12px",
      fontWeight: "400",
      padding: "4px 12px",
      minWidth: "auto",
      borderStyle: "dashed",
      "&:hover": {
        backgroundColor: "#f3f2f1",
        borderColor: "#c8c6c4",
        color: "#323130",
      },
    },
  },
  toggleButton: {
    root: {
      backgroundColor: "#f9f9f9",
      borderColor: "#e9e9e9",
      borderWidth: "2px",
      borderRadius: "7px",
      color: "#333",
      fontSize: "small",
      paddingRight: "0px",
      paddingLeft: "0px",
      paddingTop: "5px",
      paddingBottom: "5px",
      maxWidth: "45%",
    },
    rootHovered: {
      backgroundColor: "#f0f0f0",
      borderColor: "#d9d9d9",
    },
    rootChecked: {
      backgroundColor: "#8dc3fd",
      borderColor: "#007cf6",
      borderWidth: "2px",
      borderRadius: "7px",
      color: "#000",
      fontSize: "small",
      paddingRight: "0px",
      paddingLeft: "0px",
      paddingTop: "5px",
      paddingBottom: "5px",
      maxWidth: "45%",
    },
    rootCheckedHovered: {
      backgroundColor: "#a0d2ff",
      borderColor: "#005a9e",
    },
  },
};
